import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Game-like training overlay: four dimming panels leave only the current
// target interactive, the target is outlined and an animated finger points at
// it. The tour advances only through the action declared by the step (tap the
// target, type into it, or wait for the ride to change the status), so a user
// cannot skip ahead by accident. «Пропустить обучение» is always available.

const PAD = 6;
const POLL_MS = 180;
const GONE_AFTER_MS = 1400;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const inViewport = (rect) =>
  rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;

const textHas = (el, needle) => {
  if (!needle) return true;
  const text = String(el.textContent ?? "");
  return needle instanceof RegExp
    ? needle.test(text)
    : text.toLowerCase().includes(String(needle).toLowerCase());
};

const findTarget = (step) => {
  if (!step?.target) return null;
  const selectors = Array.isArray(step.target) ? step.target : [step.target];
  for (const selector of selectors) {
    const nodes = Array.from(document.querySelectorAll(selector));
    const found = nodes.find((node) => textHas(node, step.matchText));
    if (found) return found;
  }
  return null;
};

const findField = (target) => {
  if (!target) return null;
  if (target.matches?.("input, textarea")) return target;
  return target.querySelector("input, textarea");
};

const sameBox = (a, b) =>
  a && b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

const TourGuide = ({ steps, role, onFinish }) => {
  const [index, setIndex] = useState(0);
  const [box, setBox] = useState(null);
  const [viewport, setViewport] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));

  // Logical step advances synchronously: on a slow phone a burst of keystrokes
  // could reach the next step's gate before React re-rendered, and the stale
  // input gate skipped the step in between (route -> send).
  const indexRef = useRef(0);
  if (index > indexRef.current) indexRef.current = index;
  const step = steps[index];
  const stepRef = useRef(step);
  stepRef.current = steps[indexRef.current] ?? step;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;
  const doneRef = useRef(false);
  const lockRef = useRef(false);
  const seenRef = useRef(false);
  const goneSinceRef = useRef(0);
  const scrolledRef = useRef(null);

  const finish = useCallback((completed) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onFinishRef.current?.(completed);
  }, []);

  const skip = useCallback(() => finish(false), [finish]);

  const goNext = useCallback(() => {
    if (lockRef.current || doneRef.current) return;
    lockRef.current = true;
    const idx = indexRef.current;
    const current = stepsRef.current[idx];
    const last = idx >= stepsRef.current.length - 1;
    const commit = () => {
      if (doneRef.current) return;
      lockRef.current = false;
      if (last) {
        finish(true);
        return;
      }
      indexRef.current = idx + 1;
      stepRef.current = stepsRef.current[idx + 1];
      setIndex(idx + 1);
    };
    // A step that ends after typing can drop the on-screen keyboard, so the
    // next instruction and the tab bar are not squeezed by it
    if (current?.blurOnNext) document.activeElement?.blur?.();
    // The last step waits a beat: the rating request must reach the server
    // before the sandbox cleanup removes the demo order
    if (current?.holdMs) setTimeout(commit, current.holdMs);
    else setTimeout(commit, 60);
  }, [finish]);

  // Action gates: a real click inside the highlighted element, or typing into
  // its field. Capture phase, so React updates have not replaced the target yet.
  useEffect(() => {
    const onClick = (event) => {
      const current = stepRef.current;
      if (!current || current.advance !== "tap") return;
      const target = findTarget(current);
      if (!target) return;
      if (target === event.target || target.contains(event.target)) goNext();
    };
    const onInput = (event) => {
      const current = stepRef.current;
      if (!current || current.advance !== "input") return;
      const field = findField(findTarget(current));
      if (!field) return;
      if (field !== event.target && !field.contains(event.target)) return;
      if (String(field.value ?? "").trim().length < (current.minLength ?? 1)) return;
      goNext();
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("input", onInput, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("input", onInput, true);
    };
  }, [goNext]);

  // Track the target: follow scroll/resize, scroll it into view when it is off
  // screen, and advance wait-steps when the ride moves on.
  useEffect(() => {
    seenRef.current = false;
    goneSinceRef.current = 0;
    scrolledRef.current = null;
    setBox(null);
    let cancelled = false;

    const tick = () => {
      if (cancelled || doneRef.current) return;
      // Read the logical step, not the rendered one: the tick may still be
      // bound to a previous render right after an advance
      const current = stepsRef.current[indexRef.current];
      if (!current) return;
      const nextViewport = { w: window.innerWidth, h: window.innerHeight };
      setViewport((prev) => (prev.w === nextViewport.w && prev.h === nextViewport.h ? prev : nextViewport));

      // The phase this step belongs to may have passed while the user was
      // reading (a fast ride): jump ahead instead of waiting for a gone button
      if (current.unless) {
        const guard = findTarget({ target: current.unless.target });
        if (guard && textHas(guard, current.unless.await)) {
          goNext();
          return;
        }
      }

      const target = findTarget(current);
      if (target) {
        const rect = target.getBoundingClientRect();
        // A target can sit inside the viewport but behind another bar (the
        // chat input covers the bottom of the order list on short screens),
        // so check the real hit target as well and scroll it to the center
        const hit = document.elementFromPoint(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2
        );
        const covered =
          hit && hit !== target && !target.contains(hit) && !hit.closest(".tour-bubble, .tour-block");
        if ((!inViewport(rect) || covered) && scrolledRef.current !== target) {
          scrolledRef.current = target;
          target.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
          return;
        }
        seenRef.current = true;
        goneSinceRef.current = 0;
        const nextBox = {
          top: Math.max(0, rect.top - PAD),
          left: Math.max(0, rect.left - PAD),
          width: Math.min(window.innerWidth, rect.width + PAD * 2),
          height: rect.height + PAD * 2,
        };
        setBox((prev) => (sameBox(prev, nextBox) ? prev : nextBox));
        if (current.advance === "text" && textHas(target, current.await)) goNext();
        return;
      }

      setBox(null);
      if (!current.orGone || !seenRef.current) return;
      if (!goneSinceRef.current) goneSinceRef.current = Date.now();
      else if (Date.now() - goneSinceRef.current > GONE_AFTER_MS) goNext();
    };

    tick();
    const timer = setInterval(tick, POLL_MS);
    const onScroll = () => tick();
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [index, steps, goNext]);

  if (!step) return null;

  const bubbleW = Math.min(340, viewport.w - 24);
  const screenH = window.screen?.height || viewport.h;
  // With interactive-widget=resizes-content the keyboard shrinks innerHeight
  const keyboardOpen = viewport.h < screenH * 0.78;
  const bubbleMax = Math.min(viewport.h * 0.52, 420);
  const bubbleLeft = box
    ? clamp(box.left + box.width / 2 - bubbleW / 2, 12, viewport.w - bubbleW - 12)
    : (viewport.w - bubbleW) / 2;

  // Long instructions and tall targets (a whole chat, the tab bar) can leave
  // too little room next to the ring: then the bubble docks to the screen edge
  // and scrolls inside itself instead of running off the viewport.
  let bubbleStyle;
  if (box && !keyboardOpen) {
    const below = viewport.h - (box.top + box.height) - 46;
    const above = box.top - 46;
    // The head, the «Дальше» button and the skip link need their own room;
    // a squeezed side bubble would push them out of sight, so a tight screen
    // docks the whole bubble to the edge instead
    if (below >= 230 || above >= 230) {
      const placeBelow = below >= above && below >= 230;
      const room = Math.max(230, Math.min(bubbleMax, placeBelow ? below : above));
      bubbleStyle = {
        left: bubbleLeft,
        width: bubbleW,
        maxHeight: room,
        ...(placeBelow
          ? { top: box.top + box.height + 46 }
          : { bottom: viewport.h - box.top + 46 }),
      };
    } else {
      bubbleStyle = { left: bubbleLeft, width: bubbleW, bottom: 24, maxHeight: bubbleMax };
    }
  } else {
    bubbleStyle = {
      left: bubbleLeft,
      width: bubbleW,
      maxHeight: bubbleMax,
      // While typing, the field and its suggestions fill the lower half:
      // pin the instruction to the top of the visible area
      ...(keyboardOpen ? { top: 12 } : { bottom: 24 }),
    };
  }

  let handStyle = null;
  let handFlipped = false;
  if (box) {
    const handLeft = clamp(box.left + box.width / 2 - 17, 8, viewport.w - 46);
    const belowTop = box.top + box.height + 4;
    if (belowTop + 44 < viewport.h) {
      handStyle = { top: belowTop, left: handLeft };
    } else {
      handStyle = { top: Math.max(4, box.top - 44), left: handLeft };
      handFlipped = true;
    }
  }

  const block = (style) => (
    <div
      className="tour-block"
      style={style}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    />
  );

  // The dimming layer is its own fixed stacking context, so children cannot
  // out-rank the sticky header or the address suggestions panel (z 100000).
  // The hand and the bubble live in a separate body portal to stay on top.
  return (
    <>
      {createPortal(
        <div className="tour-layer" aria-hidden="true">
          {box ? (
            <>
              {block({ left: 0, top: 0, width: viewport.w, height: box.top })}
              {block({ left: 0, top: box.top + box.height, width: viewport.w, height: Math.max(0, viewport.h - box.top - box.height) })}
              {block({ left: 0, top: box.top, width: box.left, height: box.height })}
              {block({ left: box.left + box.width, top: box.top, width: Math.max(0, viewport.w - box.left - box.width), height: box.height })}
              <div
                className="tour-ring"
                style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
              />
            </>
          ) : (
            block({ inset: 0 })
          )}
        </div>,
        document.body
      )}

      {createPortal(
        <>
          {handStyle && (
            <span className={`tour-hand${handFlipped ? " is-flipped" : ""}`} style={handStyle} aria-hidden="true">
              <span>👆</span>
            </span>
          )}

          <div className="tour-bubble" role="dialog" aria-modal="true" aria-label="Обучение" style={bubbleStyle}>
            <div className="tour-bubble-head">
              <span>Шаг {index + 1} из {steps.length}</span>
              <span>{role === "driver" ? "Обучение водителя" : "Обучение пассажира"}</span>
            </div>
            <div className="tour-bubble-text" aria-live="polite">{step.text}</div>
            {!box && step.advance !== "next" && (
              <div className="tour-bubble-wait">Ждём нужный экран…</div>
            )}
            {step.advance === "next" && (
              <button type="button" className="tour-next press" onClick={goNext}>
                {index === steps.length - 1 ? "Завершить обучение" : "Дальше"}
              </button>
            )}
            <button type="button" className="tour-skip press" onClick={skip}>
              Пропустить обучение
            </button>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default TourGuide;
